import React from 'react'
import { loadStripe } from '@stripe/stripe-js/pure'
import { Elements, useStripe } from '@stripe/react-stripe-js'

import { useGetUserCountryAndStripeKeyQuery } from 'hooks/useQueries'
import { dispatchRollbarError } from 'utils/rollbar'

/*
  This pattern was suggested by a stripe engineer as a way to avoid calling loadStripe() until
  Stripe was required, and to do so only once, to improve performance:
  https://github.com/stripe/stripe-js/issues/43#issuecomment-643840075
*/

/*
  The Elements provider accepts null but not undefined for its `stripe` prop. So passing null until
  the user's API key is retrieved from the server and can be used to load the stripe instance.
*/
let stripePromise: ReturnType<typeof loadStripe> = null

const getStripePromise = (key: string) => {
  if (!stripePromise && key) {
    stripePromise = loadStripe(key)
  }

  return stripePromise
}

const LazyStripeElements: React.FC<{}> = ({ children }) => {
  const { data } = useGetUserCountryAndStripeKeyQuery({
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      dispatchRollbarError(`Failed to fetch stripe public key for user`, {
        originalError: error?.toString(),
      })
    },
  })

  const key = data?.currentUser?.defaultCountry?.stripePlatform?.publicKey

  return <Elements stripe={getStripePromise(key)}>{children}</Elements>
}

interface WithStripeProp {
  stripe: ReturnType<typeof useStripe>
}

export const withStripe = <P extends Record<string, unknown>>(
  Component: React.JSXElementConstructor<P> & WithStripeProp
) => (props: P) => {
  const stripe = useStripe()

  return <Component {...props} stripe={stripe} />
}

export default LazyStripeElements
